import {
  Component,
  EventEmitter,
  Input,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  FormGroupDirective,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { LoadingSpaceType } from 'src/app/core/types';
import { Project } from '../../../../core/objects/project';
import { Subscription } from 'rxjs';
import {
  validateMaxVarioFloorCapacity,
  validateMaxVarioFloorThickness,
  validateMinVarioFloorThickness,
} from '../../../../core/validators/validators';

@Component({
    selector: 'gl-loading-space-vario-floor',
    templateUrl: './loading-space-vario-floor.component.html',
    styleUrls: ['./loading-space-vario-floor.component.scss'],
    standalone: false
})
export class LoadingSpaceVarioFloorComponent implements OnInit, OnChanges, OnDestroy {
  @Output() public submitEvent = new EventEmitter();
  @Input() public chosenLoadingSpace: LoadingSpaceType;
  @Input() public loadingSpace: LoadingSpaceType;
  @Input() public typeLoadingSpace: number;
  @Input() public isEdit = false;
  @ViewChild('f', { static: false }) public formEl: FormGroupDirective;
  @Input() public project: Project;
  public form: FloorForm;
  public loadingSpaces: LoadingSpaceType[];
  public isTrailerOpen = true;
  public isSemitrailerOpen = false;
  public isSet = false;
  public loadingSpaceMod: LoadingSpaceType;
  private pattern = '^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$';
  private subscription: Subscription[] = [];

  constructor() {
    this.form = this.generateLoadingSpaceForm();
  }

  public ngOnInit(): void {
    this.updateSpaceMod();
    this.addMaxValidators();
    this.subscription.push(
      this.form.valueChanges.subscribe(() => {
        this.updateSpaceMod();
        this.onSubmit();
      }),
    );
    this.isSet = this.loadingSpace && this.loadingSpace.set;
    this.onChosen(this.loadingSpace);
    if (this.isEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }

    this.subscription.push(
      this.form
        .get('floorHeight')
        .valueChanges.subscribe((value) => (value ? this.addMaxValidators() : undefined)),
    );
    this.subscription.push(
      this.form
        .get('floorHeight2')
        .valueChanges.subscribe((value) => (value ? this.addMaxValidators() : undefined)),
    );
    this.subscription.push(
      this.form
        .get('floorThickness')
        .valueChanges.subscribe((value) => (value ? this.addMaxValidators() : undefined)),
    );
    this.subscription.push(
      this.form
        .get('floorThickness2')
        .valueChanges.subscribe((value) => (value ? this.addMaxValidators() : undefined)),
    );
  }

  public reload(): void {
    this.onChosen(this.chosenLoadingSpace);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.loadingSpace &&
      changes.loadingSpace.currentValue &&
      changes.loadingSpace.currentValue.height
    ) {
      this.updateSpaceMod();
      this.addMaxValidators();
    }
    if (
      changes.chosenLoadingSpace &&
      changes.chosenLoadingSpace.currentValue !== changes.chosenLoadingSpace.previousValue
    ) {
      this.onChosen(changes.chosenLoadingSpace.currentValue as LoadingSpaceType);
    }
    this.isSet = changes.loadingSpace && changes.loadingSpace.currentValue.set;
    if (changes.typeLoadingSpace && !changes.typeLoadingSpace.firstChange) {
      this.form.reset(undefined, { onlySelf: true, emitEvent: false });
    }
    if (changes.isEdit && changes.isEdit.currentValue) {
      this.isEdit = true;
      this.form.enable();
    } else if (changes.isEdit && !changes.isEdit.currentValue) {
      this.isEdit = false;
      this.form.disable();
    }
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((s) => s.unsubscribe());
  }

  private setControlValidator(name: string, max: number, min: number): void {
    this.form.controls[name].clearValidators();
    const validations: ValidatorFn[] = [Validators.pattern(this.pattern), Validators.required];
    name.includes('floorHeight') ? validations.push(Validators.max(max)) : undefined;
    name.includes('floorThickness')
      ? validations.push(validateMaxVarioFloorThickness(max))
      : undefined;
    name.includes('floorMaximumCapacity')
      ? validations.push(validateMaxVarioFloorCapacity(max))
      : undefined;
    name.includes('floorThickness')
      ? validations.push(validateMinVarioFloorThickness(min))
      : validations.push(Validators.min(min));
    this.form.get(name).setValidators(validations);
    this.form.controls[name].updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private addMaxValidators(): void {
    if (!this.loadingSpace) {
      return;
    }
    const floorThickness = 10;
    if (this.loadingSpace.width && this.loadingSpace.height) {
      const maxHeight = this.form.get('floorHeight').value
        ? parseFloat(this.form.get('floorHeight').value) < this.loadingSpace.height
          ? parseFloat(this.form.get('floorHeight').value)
          : this.loadingSpace.height
        : this.loadingSpace.height;
      const minFloorThickness = this.form.get('floorThickness').value
        ? parseFloat(this.form.get('floorThickness').value)
        : floorThickness;

      this.setControlValidator('floorHeight', this.loadingSpace.height, minFloorThickness);
      this.setControlValidator('floorThickness', maxHeight, 1);
    }

    if (this.loadingSpace.limit) {
      this.setControlValidator('floorMaximumCapacity', this.loadingSpace.limit, 0);
    }

    if (this.loadingSpace.width1 && this.loadingSpace.height1) {
      const maxHeight = this.form.get('floorHeight2').value
        ? parseFloat(this.form.get('floorHeight2').value) < this.loadingSpace.height1
          ? parseFloat(this.form.get('floorHeight2').value)
          : this.loadingSpace.height1
        : this.loadingSpace.height1;
      const minFloorThickness = this.form.get('floorThickness2').value
        ? parseFloat(this.form.get('floorThickness2').value)
        : floorThickness;
      this.setControlValidator('floorHeight2', this.loadingSpace.height1, minFloorThickness);
      this.setControlValidator('floorThickness2', maxHeight, 1);
    }
    if (this.loadingSpace.limit1) {
      this.setControlValidator('floorMaximumCapacity2', this.loadingSpace.limit1, 0);
      const maxHeight = this.loadingSpace.height1;
      this.setControlValidator('floorHeight2', maxHeight, floorThickness);
    }
  }

  private updateSpaceMod(): void {
    const floorHeightFormValue = this.form.get('floorHeight').value as string;
    const floorThicknessFormValue = this.form.get('floorThickness').value as string;
    const floorMaximumCapacityFormValue = this.form.get('floorMaximumCapacity').value as string;
    const floorHeight2FormValue = this.form.get('floorHeight2').value as string;
    const floorThickness2FormValue = this.form.get('floorThickness').value as string;
    const floorMaximumCapacity2FormValue = this.form.get('floorMaximumCapacity').value as string;
    const floorHeight =
      floorHeightFormValue !== null || floorHeightFormValue !== ''
        ? parseFloat(this.form.get('floorHeight').value)
        : null;
    const floorHeight2 =
      floorHeight2FormValue !== null || floorHeight2FormValue !== ''
        ? parseFloat(this.form.get('floorHeight2').value)
        : null;
    const varioTrailerFloorThickness =
      floorThicknessFormValue !== null || floorThicknessFormValue !== ''
        ? parseFloat(this.form.get('floorThickness').value)
        : null;
    const varioSemiTrailerFloorThickness =
      floorThickness2FormValue !== null || floorThickness2FormValue !== ''
        ? parseFloat(this.form.get('floorThickness2').value)
        : null;
    const varioTrailerFloorCapacity =
      floorMaximumCapacityFormValue !== null || floorMaximumCapacityFormValue !== ''
        ? parseFloat(this.form.get('floorMaximumCapacity').value)
        : null;
    const varioSemiTrailerFloorCapacity =
      floorMaximumCapacity2FormValue !== null || floorMaximumCapacity2FormValue !== ''
        ? parseFloat(this.form.get('floorMaximumCapacity2').value)
        : null;

    this.loadingSpaceMod = {
      name: this.loadingSpace.name,
      length: this.loadingSpace.length,
      width: this.loadingSpace.width,
      height: this.loadingSpace.height,
      limit: this.loadingSpace.limit,
      length1: this.loadingSpace.length1,
      width1: this.loadingSpace.width1,
      height1: this.loadingSpace.height1,
      limit1: this.loadingSpace.limit1,
      set: true,
      type: this.loadingSpace.type,
      id: null,
      unit: this.loadingSpace.unit,
      axis: [],
      useInRecommendation: null,
      wheelTrailerWidth: null,
      wheelTrailerHeight: null,
      wheelTrailerLength: null,
      wheelTrailerDistance: null,
      wheelSemiTrailerWidth: null,
      wheelSemiTrailerHeight: null,
      wheelSemiTrailerLength: null,
      wheelSemiTrailerDistance: null,
      coolerTrailerWidth: null,
      coolerTrailerHeight: null,
      coolerTrailerLength: null,
      coolerTrailerFloorHeight: null,
      coolerSemiTrailerWidth: null,
      coolerSemiTrailerHeight: null,
      coolerSemiTrailerLength: null,
      coolerSemiTrailerFloorHeight: null,
      varioSemiTrailerFloorHeight: floorHeight2,
      varioTrailerFloorHeight: floorHeight,
      varioTrailerFloorThickness,
      varioSemiTrailerFloorThickness,
      varioTrailerFloorCapacity,
      varioSemiTrailerFloorCapacity,
    };
  }

  public onSubmit(): void {
    if (this.form.dirty) {
      this.formEl?.onSubmit(undefined);
    }
    const isTrailerValid =
      this.form.get('floorHeight').valid &&
      this.form.get('floorThickness').valid &&
      this.form.get('floorMaximumCapacity').valid;
    const isSemiTrailerValid =
      this.form.get('floorHeight2').valid &&
      this.form.get('floorThickness2').valid &&
      this.form.get('floorMaximumCapacity2').valid;
    if ((this.isSet && isSemiTrailerValid) || (this.isSet && isTrailerValid)) {
      this.submitEvent.emit(this.form.value);
    } else if (!this.isSet && isTrailerValid) {
      this.submitEvent.emit(this.form.value);
    }
  }

  public onChosen(loadingSpace: LoadingSpaceType): void {
    if (!loadingSpace) {
      return;
    }
    if (loadingSpace.varioTrailerFloorHeight) {
      this.form.get('floorHeight').patchValue(loadingSpace.varioTrailerFloorHeight);
      this.form.get('floorThickness').patchValue(loadingSpace.varioTrailerFloorThickness);
      this.form.get('floorMaximumCapacity').patchValue(loadingSpace.varioTrailerFloorCapacity);
    } else {
      this.form.get('floorHeight').patchValue(null);
      this.form.get('floorThickness').patchValue(null);
      this.form.get('floorMaximumCapacity').patchValue(null);
    }
    if (loadingSpace.varioSemiTrailerFloorHeight) {
      this.form.get('floorHeight2').patchValue(loadingSpace.varioSemiTrailerFloorHeight);
      this.form.get('floorThickness2').patchValue(loadingSpace.varioSemiTrailerFloorThickness);
      this.form.get('floorMaximumCapacity2').patchValue(loadingSpace.varioSemiTrailerFloorCapacity);
    } else {
      this.form.get('floorHeight2').patchValue(null);
      this.form.get('floorThickness2').patchValue(null);
      this.form.get('floorMaximumCapacity2').patchValue(null);
    }
  }

  public formReset(): void {
    this.form.patchValue({
      floorHeight: null,
      floorHeight2: null,
      floorThickness: null,
      floorThickness2: null,
      floorMaximumCapacity: null,
      floorMaximumCapacity2: null,
    });
    this.submitEvent.emit(this.form.value);
  }

  public openTab(tabName: string): void {
    this.isTrailerOpen = false;
    this.isSemitrailerOpen = false;
    switch (tabName) {
      case 'trailer':
        this.isTrailerOpen = true;
        break;
      case 'semitrailer':
        this.isSemitrailerOpen = true;
        break;
    }
  }

  private generateLoadingSpaceForm(): FloorForm {
    return new UntypedFormGroup({
      floorHeight: new UntypedFormControl(null),
      floorHeight2: new UntypedFormControl(null),
      floorThickness: new UntypedFormControl(null),
      floorThickness2: new UntypedFormControl(null),
      floorMaximumCapacity: new UntypedFormControl(null),
      floorMaximumCapacity2: new UntypedFormControl(null),
    });
  }
}

interface FloorForm extends UntypedFormGroup {
  value: {
    floorHeight: number;
    floorHeight2: number;
    floorThickness: number;
    floorThickness2: number;
    floorMaximumCapacity: number;
    floorMaximumCapacity2: number;
  };
}
